import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Wallet, ArrowRight, X, AlertCircle, Coins, Sparkles, ArrowDownToLine } from 'lucide-react';

/**
 * NoGameBalanceModal (Iter 145+)
 * ==============================
 * Replaces boring `toast.error("No balance to redeem")` pop-overs with a
 * polished, animated, theme-matching dialog. Two variants:
 *
 *   variant="load"    — Player tried to load credits with $0 in their wallet.
 *                       Headline: "No balance to load in game"
 *                       CTA:      "Deposit Now →"  ⇒  routes to deposit flow
 *
 *   variant="redeem"  — Player tried to redeem with $0 in the game.
 *                       Headline: "No balance to redeem in game"
 *                       CTA:      "Got it"  (closes only)
 *
 * Animation: spring-like scale + fade-in entrance, gradient orb pulse, soft
 * confetti dots floating around the icon. CSS-only — no Framer dependency.
 *
 * Props:
 *   variant       'load' | 'redeem'
 *   open          boolean — controls visibility
 *   onClose       () => void
 *   onDepositNow  () => void   (only used in 'load' variant)
 *   subtitle      optional override for the secondary line
 */
export default function NoGameBalanceModal({
  variant = 'load',
  open,
  onClose,
  onDepositNow,
  subtitle,
}) {
  const depositClickedRef = useRef(false);

  // Esc closes
  useEffect(() => {
    if (!open) return undefined;
    depositClickedRef.current = false;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const isLoad = variant === 'load';
  // Theme-matching palettes — load uses neon-green (deposit/credit cue),
  // redeem uses amber-gold (cashout cue).
  const accent = isLoad ? '#004D61' : '#FCD34D';
  const accentDeep = isLoad ? '#15803D' : '#B45309';
  const accentSoft = isLoad ? 'rgba(0,77,97,0.18)' : 'rgba(252,211,77,0.20)';
  const Icon = isLoad ? Wallet : ArrowDownToLine;
  const closeModal = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onClose?.();
  };

  const depositNow = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (depositClickedRef.current) return;
    depositClickedRef.current = true;
    onDepositNow?.();
  };

  const blockBackdropClick = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
  };

  const overlay = (
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center px-4"
      style={{
        background: 'rgba(0,0,0,0.78)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        animation: 'no-bal-overlay-in 200ms cubic-bezier(0.16, 1, 0.3, 1) both',
        pointerEvents: 'auto',
        touchAction: 'none',
      }}
      onClick={blockBackdropClick}
      data-testid="no-balance-modal-overlay"
    >
      <div
        className="relative w-full max-w-sm rounded-[28px] p-7 sm:p-8 overflow-hidden text-center"
        style={{
          background: 'linear-gradient(165deg, #0B0F0C 0%, #050A07 100%)',
          border: `1.5px solid ${accent}55`,
          boxShadow: `0 30px 80px rgba(0,0,0,0.7), 0 0 0 1px ${accent}33, 0 0 60px ${accent}33`,
          animation: 'no-bal-modal-in 360ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="no-balance-modal"
        data-variant={variant}
      >
        {/* Floating confetti dots — purely decorative */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          {[
            { l: '15%', t: '18%', d: '0s', s: 6 },
            { l: '85%', t: '22%', d: '0.4s', s: 4 },
            { l: '78%', t: '70%', d: '0.8s', s: 7 },
            { l: '12%', t: '74%', d: '0.2s', s: 5 },
            { l: '50%', t: '8%', d: '0.6s', s: 3 },
          ].map((p, i) => (
            <span
              key={i}
              className="absolute rounded-full"
              style={{
                left: p.l, top: p.t, width: p.s, height: p.s,
                background: accent, boxShadow: `0 0 12px ${accent}`,
                animation: `no-bal-float 2.6s ease-in-out infinite ${p.d}`,
                opacity: 0.45,
              }}
            />
          ))}
        </div>

        {/* Close button */}
        <button
          type="button"
          onClick={closeModal}
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition hover:bg-white/10"
          style={{ color: 'rgba(255,255,255,0.45)', pointerEvents: 'auto', touchAction: 'manipulation' }}
          aria-label="Close"
          data-testid="no-balance-modal-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Animated icon orb */}
        <div className="relative mx-auto mb-5 flex items-center justify-center">
          <div
            className="absolute rounded-full"
            style={{
              width: 130, height: 130,
              background: `radial-gradient(circle, ${accent}40 0%, transparent 65%)`,
              animation: 'no-bal-pulse 2.8s ease-in-out infinite',
            }}
          />
          <div
            className="relative w-[88px] h-[88px] rounded-full flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${accent}, ${accentDeep})`,
              boxShadow: `0 14px 40px ${accent}55, inset 0 2px 0 rgba(255,255,255,0.35), inset 0 -2px 6px rgba(0,0,0,0.25)`,
              animation: 'no-bal-bob 3.2s ease-in-out infinite',
            }}
          >
            <Icon className="w-10 h-10 text-black" strokeWidth={2.4} />
            {/* AlertCircle badge */}
            <span
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center"
              style={{
                background: '#0B0F0C',
                border: `2px solid ${accent}`,
                boxShadow: `0 4px 12px ${accent}66`,
              }}
            >
              <AlertCircle className="w-4 h-4" style={{ color: accent }} strokeWidth={2.5} />
            </span>
          </div>
        </div>

        {/* Headline + subtitle */}
        <p
          className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2"
          style={{ color: accent, opacity: 0.85 }}
          data-testid="no-balance-modal-eyebrow"
        >
          {isLoad ? 'Wallet empty' : 'Game empty'}
        </p>
        <h2
          className="display text-2xl sm:text-[26px] font-extrabold leading-tight text-white mb-2"
          data-testid="no-balance-modal-title"
        >
          {isLoad
            ? 'No balance to load in game'
            : 'No balance to redeem in game'}
        </h2>
        <p
          className="text-sm leading-relaxed mb-6"
          style={{ color: 'rgba(255,255,255,0.7)' }}
          data-testid="no-balance-modal-subtitle"
        >
          {subtitle || (isLoad
            ? 'Top up your main wallet first, then come back to load credits into the game and start playing.'
            : 'Your game balance is $0.00. Win some credits in-game first, then redeem them back to your wallet.')}
        </p>

        {/* CTA */}
        {isLoad ? (
          <button
            type="button"
            onClick={depositNow}
            className="group w-full h-14 rounded-2xl display font-extrabold text-base flex items-center justify-center gap-2 transition active:scale-[0.98] hover:brightness-110 relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${accent} 0%, ${accentDeep} 100%)`,
              color: '#000',
              boxShadow: `0 10px 28px ${accent}66, inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -2px 6px rgba(0,0,0,0.18)`,
              pointerEvents: 'auto',
              touchAction: 'manipulation',
            }}
            data-testid="no-balance-modal-deposit-btn"
          >
            {/* Shimmer sweep */}
            <span
              className="pointer-events-none absolute inset-0"
              style={{
                background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.45) 50%, transparent 70%)',
                animation: 'no-bal-shimmer 2.4s ease-in-out infinite',
              }}
              aria-hidden
            />
            <Coins className="w-5 h-5 relative" strokeWidth={2.6} />
            <span className="relative">Deposit Now</span>
            <ArrowRight className="w-5 h-5 relative transition-transform group-hover:translate-x-0.5" strokeWidth={2.6} />
          </button>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="w-full h-14 rounded-2xl display font-extrabold text-base transition active:scale-[0.98] hover:brightness-110"
            style={{
              background: `linear-gradient(135deg, ${accent} 0%, ${accentDeep} 100%)`,
              color: '#000',
              boxShadow: `0 10px 28px ${accent}55`,
            }}
            data-testid="no-balance-modal-acknowledge-btn"
          >
            <span className="inline-flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5" strokeWidth={2.6} /> Got it
            </span>
          </button>
        )}
      </div>

      {/* Component-scoped keyframes (declared once on first mount) */}
      <style>{`
        @keyframes no-bal-overlay-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes no-bal-modal-in {
          0%   { opacity: 0; transform: scale(0.86) translateY(20px); }
          60%  { opacity: 1; transform: scale(1.02) translateY(-4px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes no-bal-pulse {
          0%, 100% { transform: scale(1); opacity: 0.55; }
          50%      { transform: scale(1.18); opacity: 0.85; }
        }
        @keyframes no-bal-bob {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50%      { transform: translateY(-4px) rotate(-3deg); }
        }
        @keyframes no-bal-shimmer {
          0%   { transform: translateX(-110%); }
          50%  { transform: translateX(110%); }
          100% { transform: translateX(110%); }
        }
        @keyframes no-bal-float {
          0%, 100% { transform: translate(0, 0); opacity: 0.45; }
          50%      { transform: translate(8px, -10px); opacity: 0.85; }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="no-balance-modal"],
          [data-testid="no-balance-modal-overlay"] { animation-duration: 1ms !important; }
          [data-testid="no-balance-modal"] * { animation: none !important; }
        }
      `}</style>
    </div>
  );

  const host = typeof document !== 'undefined' ? document.body : null;
  return host ? createPortal(overlay, host) : overlay;
}
